"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StoreMenuScene = void 0;
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const i18next_1 = __importDefault(require("i18next"));
const blocks_1 = require("../blocks");
const interfaces_1 = require("../../../shared/interfaces");
const StoreButton_1 = require("../blocks/StoreButton");
const ui_1 = require("@pixi/ui");
const store_1 = require("../../../shared/store");
const brushpoint_svg_1 = __importDefault(require("../../assets/svg/brushpoint.svg"));
const ConfirmPopup_1 = require("../popups/ConfirmPopup");
const settings_1 = require("../config/settings");
const IconButton_1 = require("../blocks/IconButton");
const noads_svg_1 = __importDefault(require("../../assets/svg/noads.svg"));
const MenuScene_1 = require("./MenuScene");
const gsap_1 = require("gsap");
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class StoreMenuScene extends MenuScene_1.MenuScene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager);
        this.itemmargin = 20;
        this.zIndex = SceneManager_1.Layer.Menu;
        this.colortext = this.addChild(new pixi_js_1.Text(i18next_1.default.t('mainmenu.colors'), blocks_1.Block.textStyle(32)));
        if (config_json_1.default.purchase && (0, Global_1.isDevice)()) {
            const button = this.makeRemoveAdsButton().asButton(0.01);
            this.removeAdsButton = this.addChild(button);
            this.removeAdsButton.scale.set(0.99);
            this.sigs.push(button.onPress.connect(() => {
                // Pay up to remove the ads!
            }));
            button.visible = false;
            this.subs.add(this.gameManager.purchaseManager.storeOpen$
                .subscribe((is) => __awaiter(this, void 0, void 0, function* () {
                const available = is && this.gameManager.purchaseManager.canPurchase(interfaces_1.ProductItem.Test);
                yield (0, Global_1.fade)(this.removeAdsButton, available ? 1 : 0);
            })));
        }
        if (config_json_1.default.brushreward && (0, Global_1.isPoki)()) {
            const rbutton = this.makeRewardBrushButton().asButton(0.01);
            this.adsToBrushButton = this.addChild(rbutton);
            this.adsToBrushButton.scale.set(0.99);
            this.sigs.push(rbutton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
                // Brush for Ads
                rbutton.enabled = false;
                if (yield this.gameManager.adManager.triggerReward(null)) {
                    // Watched the ad, give brushes
                    this.gameManager.playerManager.reward();
                }
                else {
                    // Could not load ad?
                    // Poki handles this it seems
                    // this.makeRewardErrorPopup();
                }
                this.layout();
                rbutton.enabled = true;
            })));
        }
        this.scrollBox = new ui_1.ScrollBox({
            elementsMargin: (0, Global_1.px)(this.itemmargin),
            width: Math.min(this.app.screen.width, (0, Global_1.px)(settings_1.Settings.maxWidth)),
            height: this.app.screen.height,
            bottomPadding: (0, Global_1.px)(20),
            topPadding: (0, Global_1.px)(10),
            disableEasing: false,
            type: undefined,
            globalScroll: true,
            shiftScroll: false,
            disableDynamicRendering: false,
            items: this.makeColorItems()
        });
        this.addChild(this.scrollBox);
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const top = _super.enterScene.call(this, from, target);
            const button = this.adsToBrushButton || this.removeAdsButton;
            if (button) {
                const cur = button.scale.clone();
                button.scale.set((0, Global_1.px)(0.001));
                this.promisedTimeline(gsap_1.gsap.timeline().to(button, {
                    pixi: {
                        rotation: 0,
                        scaleX: cur.x,
                        scaleY: cur.y
                    },
                    duration: 0.4,
                    ease: 'bounce'
                })).then();
            }
            return top;
        });
    }
    makeRemoveAdsButton() {
        const sprite = pixi_js_1.Sprite.from(noads_svg_1.default);
        sprite.scale.set((0, Global_1.px)(0.17));
        const text = new pixi_js_1.Text(i18next_1.default.t('removeads'), blocks_1.Block.textStyle(24));
        const b = new IconButton_1.IconButton(this.app, text, sprite, 4);
        b.color = settings_1.Settings.adsColor;
        b.rotation = -Math.PI / 40;
        return b;
    }
    makeRewardBrushButton() {
        const sprite = pixi_js_1.Sprite.from(brushpoint_svg_1.default);
        sprite.scale.set((0, Global_1.px)(0.18));
        const text = new pixi_js_1.Text('🎬 ' + i18next_1.default.t('for') + ' +' + config_json_1.default.brushreward, blocks_1.Block.textStyle(26));
        const b = new IconButton_1.IconButton(this.app, text, sprite, 4);
        b.color = settings_1.Settings.rewardColor;
        b.rotation = -Math.PI / 65;
        return b;
    }
    makeColorItems() {
        const items = [];
        const group = store_1.stores[0];
        let selected = null;
        group.items.filter((item) => !item.hidden).forEach((item) => {
            var _a;
            const storeitem = new StoreButton_1.StoreButton(this.app, this.gameManager, group, item);
            const button = storeitem.asButton(0.01);
            button.pivot.set(button.width / 2, button.height / 2);
            button.position.set(button.width / 2, button.height / 2);
            const selectit = () => {
                selected === null || selected === void 0 ? void 0 : selected.deselect();
                this.gameManager.playerManager.updateLabel({ [group.name]: (item.value) });
                selected = storeitem.select();
                AssetManager_1.default.tickSound();
            };
            this.sigs.push(button.onPress.connect(() => {
                var _a, _b, _c;
                const pos = { x: -((_a = this.scrollBox) === null || _a === void 0 ? void 0 : _a.scrollX), y: -((_b = this.scrollBox) === null || _b === void 0 ? void 0 : _b.scrollY) };
                if (!storeitem.owns) {
                    if (storeitem.afford) {
                        if (item.cost_brushes) {
                            this.makeConfirmPopup(group, item, storeitem);
                        }
                        else {
                            this.buy(group, item);
                        }
                    }
                    else {
                        // Popup
                        this.makeAffordPopup(item, storeitem);
                    }
                    this.layout();
                }
                else {
                    selectit();
                }
                (_c = this.scrollBox) === null || _c === void 0 ? void 0 : _c.scrollToPosition(pos);
            }));
            if (!selected && storeitem.owns && ((_a = this.gameManager.playerManager.data) === null || _a === void 0 ? void 0 : _a[group.name]) === item.value) {
                selected = storeitem.select();
            }
            const container = new pixi_js_1.Container();
            container.addChild(button);
            items.push(container);
        });
        return items;
    }
    makeConfirmPopup(group, item, button) {
        const coreitem = button.makeItem();
        coreitem.scale.set(1.45);
        const popup = new ConfirmPopup_1.ConfirmPopup(this.app, i18next_1.default.t('mainmenu.storeconfirm', { value: button.name }), i18next_1.default.t('yes'), i18next_1.default.t('no'), coreitem);
        popup.confirm = () => {
            this.buy(group, item);
        };
        AssetManager_1.default.untickSound();
        // Append to main stage
        return this.addChild(popup);
    }
    makeRewardErrorPopup() {
        const sprite = pixi_js_1.Sprite.from(noads_svg_1.default);
        sprite.scale.set((0, Global_1.px)(0.5));
        const popup = new ConfirmPopup_1.ConfirmPopup(this.app, i18next_1.default.t('mainmenu.noad'), i18next_1.default.t('close'), null, sprite);
        // Append to main stage
        return this.addChild(popup);
    }
    makeAffordPopup(item, button) {
        const sprite = pixi_js_1.Sprite.from(item.cost_brushes ? AssetManager_1.default.brushpoint : AssetManager_1.default.fire);
        sprite.scale.set((0, Global_1.px)(item.cost_brushes ? 0.6 : 0.46));
        let txt;
        if (item.cost_brushes) {
            txt = i18next_1.default.t('mainmenu.need', { count: (item.cost_brushes
                    - this.gameManager.playerManager.brushes), value: button.name });
        }
        else {
            txt = i18next_1.default.t('mainmenu.needstreak', { count: (item.cost_streak
                    - this.gameManager.playerManager.streak), value: button.name });
        }
        const popup = new ConfirmPopup_1.ConfirmPopup(this.app, txt, i18next_1.default.t('close'), null, sprite);
        AssetManager_1.default.untickSound();
        // Append to main stage
        return this.addChild(popup);
    }
    buy(group, item) {
        this.gameManager.playerManager.buy(group.name, item.value);
        AssetManager_1.default.squishSound();
    }
    destroy(_options) {
        // Because our ui scrollbox operates outside our "Block" system, we have to destroy it manually
        // There may be a bug in the scrollbox but the list item children are not getting destroyed too
        this.scrollBox.items.forEach((item) => {
            ((item.getChildAt(0)).getChildAt(0)).destroy({ children: true });
        });
        super.destroy(_options);
    }
    layout() {
        var _a, _b;
        super.layout();
        const landscape = this.isLandscapeBox;
        if (landscape) {
            this.colortext.position.set((0, Global_1.px)(settings_1.Settings.navHeight + 10), (0, Global_1.px)(30));
            this.scrollBox.position.set((0, Global_1.px)(settings_1.Settings.navHeight + 10), this.colortext.y + (0, Global_1.px)(50));
        }
        else {
            this.colortext.position.set(((0, Global_1.isrtl)() ? Global_1.right : Global_1.left)(this.app.screen.width, 40) - ((0, Global_1.isrtl)() ? this.colortext.width : 0), (0, Global_1.px)(80));
            this.scrollBox.position.set((0, Global_1.left)(this.app.screen.width), this.colortext.y + (0, Global_1.px)(50));
        }
        // Should only show one...
        const adsx = this.colortext.x + (0, Global_1.px)(150) + this.colortext.width;
        const adsy = this.colortext.y + (0, Global_1.px)(10);
        (_a = this.removeAdsButton) === null || _a === void 0 ? void 0 : _a.position.set(adsx, adsy);
        (_b = this.adsToBrushButton) === null || _b === void 0 ? void 0 : _b.position.set(adsx, adsy);
        // It is important to make sure that all items in the scrollbox are the same size-ish
        let itemwidth = 1;
        this.scrollBox.items.forEach((item) => {
            const sitem = ((item.getChildAt(0)).getChildAt(0));
            sitem.layout();
            itemwidth = sitem.width + (0, Global_1.px)(this.itemmargin);
        });
        // We have to hand calculate the number of items in each row because there is no easy way to do this in scrollbox
        if (landscape) {
            this.scrollBox.width = this.app.screen.width - this.colortext.x;
            this.scrollBox.height = this.app.screen.height - this.scrollBox.y;
        }
        else {
            // Attempt to Center the scrollbox in the screen...
            const maxwidth = Math.min(this.app.screen.width, (0, Global_1.px)(settings_1.Settings.maxWidth));
            const remainingspace = (maxwidth + (0, Global_1.px)(this.itemmargin)) % itemwidth;
            this.scrollBox.width = Math.min(this.app.screen.width - remainingspace / 2 + (0, Global_1.px)(1), (0, Global_1.px)(settings_1.Settings.maxWidth));
            this.scrollBox.height = this.app.screen.height - this.scrollBox.y - (0, Global_1.px)(settings_1.Settings.navHeight - 1);
            this.scrollBox.x += remainingspace / 2;
        }
    }
}
exports.StoreMenuScene = StoreMenuScene;
